import { LiveChatWidget, WidgetState } from '@livechat/widget-react'
import { ReactNode, useCallback, useEffect, useState } from 'react'

import { useSessionEntity } from '@context/EntityContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { useOnCloseLiveChat, useOnEntitySelected, useOnOpenLiveChat } from '@utils/cookie.utils'

import { Locale } from '@global/locale/Locale'

import { useFunctionalConsent } from '@analytics/consent'

export const useLiveChatWidget = (): {
  liveChatWidget: ReactNode | null
  openLiveChat: () => void
  closeLiveChat: () => void
  isLiveChatVisible: boolean
} => {
  const [email, setEmail] = useState<string>()
  const [name, setName] = useState<string>()
  const [visibility, setVisibility] = useState<WidgetState['visibility']>('minimized')
  const [loaded, setLoaded] = useState(false)
  const [group, setGroup] = useState<string>()
  const [isVisible, setIsVisible] = useState(false)

  const entity = useSessionEntity()
  const locale = useSessionLanguage()
  const hasFunctionalConsent = useFunctionalConsent()

  const openChat = useCallback(() => {
    setIsVisible(true)
    setVisibility('maximized')
  }, [])

  const closeChat = useCallback(() => {
    setIsVisible(false)
    setVisibility('minimized')
  }, [])

  useOnOpenLiveChat(openChat)
  useOnCloseLiveChat(closeChat)

  const emptyReturn = {
    liveChatWidget: null,
    openLiveChat: useCallback(() => {}, []),
    closeLiveChat: useCallback(() => {}, []),
    isLiveChatVisible: false,
  }

  useEffect(() => {
    const entityRecord = EntityToGroupId[entity]
    if (entityRecord) {
      setGroup(entityRecord[locale])
    }
  }, [entity, locale])

  useOnEntitySelected((entity, locale, newEmail, firstName) => {
    if (newEmail) {
      setEmail(newEmail)
    }
    if (firstName) {
      setName(firstName)
    }
    const entityRecord = EntityToGroupId[entity]
    if (entityRecord) {
      setGroup(entityRecord[locale])
    }
  })

  const isLiveChatVisible = isVisible && visibility === 'maximized' && loaded

  useEffect(() => {
    if (visibility === 'minimized' && loaded && isVisible) {
      setIsVisible(false)
    }
  }, [visibility, loaded, isVisible])

  if (entity === TickmillCompaniesEnum.TICKMILL_EU || !hasFunctionalConsent) {
    return emptyReturn
  }

  return {
    liveChatWidget: (
      <LiveChatWidget
        customerEmail={email}
        customerName={name}
        sessionVariables={{ chatId: '1' }}
        visibility={isVisible ? 'maximized' : 'hidden'}
        license='11464573'
        group={group}
        onReady={() => setLoaded(true)}
        onVisibilityChanged={({ visibility }) => setVisibility(visibility)}
      />
    ),
    openLiveChat: openChat,
    closeLiveChat: closeChat,
    isLiveChatVisible,
  }
}

const EntityToGroupId: Record<TickmillCompaniesEnum, Partial<Record<Locale, string>> | undefined> =
  {
    [TickmillCompaniesEnum.TICKMILL_SC]: {
      en: '2',
      pl: '13',
      ar: '5',
      ru: '19',
      id: '8',
      de: '4',
      zh: '6',
      tr: '17',
      pt: '36',
      it: '9',
      es: '15',
      vi: '18',
      ms: '12',
      ko: '11',
      th: '16',
    },
    [TickmillCompaniesEnum.TICKMILL_UK]: {
      en: '38',
      pl: '43',
      de: '41',
      zh: '40',
      ar: '39',
      ru: '46',
      tr: '49',
      pt: '44',
      it: '42',
      es: '45',
      ko: '38',
      th: '38',
      id: '38',
      ms: '38',
      vi: '38',
    },
    [TickmillCompaniesEnum.TICKMILL_EU]: {
      en: '22',
      pl: '20',
      de: '52',
      ru: '29',
      pt: '31',
      it: '30',
      es: '32',
      ar: '22',
      zh: '22',
      tr: '22',
      id: '22',
      ms: '22',
      vi: '22',
      ko: '22',
      th: '22',
    },
    [TickmillCompaniesEnum.TICKMILL_AS]: {
      th: '16',
      en: '2',
      pl: '2',
      de: '2',
      ru: '2',
      it: '2',
      es: '2',
      ar: '2',
      zh: '2',
      tr: '2',
      id: '2',
      pt: '2',
      ms: '2',
      vi: '2',
      ko: '2',
    },
    [TickmillCompaniesEnum.TICKMILL_UAE]: undefined,
    [TickmillCompaniesEnum.TICKMILL_PA]: undefined,
    [TickmillCompaniesEnum.GENERIC]: undefined,
  }
